import {Component, inject, signal, Signal, viewChild, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {DxPaginationModule, DxTextBoxModule} from 'devextreme-angular';
import {ValueChangedEvent} from 'devextreme/ui/text_box';
import {ToastService} from '../../../core/services/technique/toast.service';
import {GenericDatagridService} from '../../../core/services/generics/generic-datagrid.service';
import {ProduitDTO} from '../../../core/dtos/produit-dto';
import {ProduitDeclinaisonDTO} from '../../../core/dtos/produit-declinaison-dto';
import {PlcProduitComplementaireDTO} from '../../../core/dtos/plc-produit-complementaire-dto';
import {
  PeriodeConditionnementDTO
} from '../../../core/dtos/produit/declinaison/complementaire/periode-conditionnement-dto';
import {JourToggle} from '../../../core/models/gestion-produits/declinaison/complementaire/plc/jour-toggle';
import {
  PRODUIT_COMPLEMENTAIRE_PAGE_SIZE
} from '../../../core/services/entities/plc-produit-complementaire.service';
import {
  ProduitProduitComplementaireService
} from '../../../core/services/entities/produit-produit-complementaire.service';
import {
  ProduitComplementaireParametrageService
} from '../../../core/services/entities/produit-complementaire-parametrage.service';
import {ProduitSupplier} from '../../../core/services/entities/produits.service';
import {MSG_KEY, MSG_SEVERITY} from '../../../core/constants';
import {ButtonThemeEnum} from '../../../shared/ui/button/button-theme-enum';
import {SharedModule} from '../../../shared/shared.module';
import {
  ProduitComplementaireGridComponent
} from '../../../shared/produits-complementaires/produit-complementaire-grid/produit-complementaire-grid.component';
import {
  DialogPeriodeConditionnementComponent
} from '../../../shared/produits-complementaires/dialog-periode-conditionnement/dialog-periode-conditionnement.component';
import {DialogAjoutAssociationComponent} from './dialog-ajout-association/dialog-ajout-association.component';

/**
 * Onglet « Produits complémentaires » de la fiche produit : effet miroir du paramétrage accessible depuis le point
 * de livraison. Une ligne vaut une association (point de livraison, produit décliné complémentaire), et les deux
 * écrans écrivent le même paramétrage.
 */
@Component({
  selector: 'yo-produit-produits-complementaires',
  templateUrl: './produit-produits-complementaires.component.html',
  styleUrls: ['./produit-produits-complementaires.component.scss'],
  standalone: true,
  imports: [DxPaginationModule, DxTextBoxModule, SharedModule, ProduitComplementaireGridComponent,
            DialogAjoutAssociationComponent, DialogPeriodeConditionnementComponent],
  encapsulation: ViewEncapsulation.None
})
export class ProduitProduitsComplementairesComponent {

  readonly PAGE_SIZE = PRODUIT_COMPLEMENTAIRE_PAGE_SIZE;
  readonly ButtonThemeEnum = ButtonThemeEnum;

  /** Clé composite : le point de livraison ne suffit pas à identifier une ligne, le produit décliné non plus. */
  readonly KEY_EXPR = ['plcId', 'produitDeclinaisonId'];

  private readonly gds = inject(GenericDatagridService);
  private readonly produitComplementaireSvc = inject(ProduitProduitComplementaireService);
  private readonly route = inject(ActivatedRoute);
  private readonly toastSvc = inject(ToastService);
  private readonly paramSvc = inject(ProduitComplementaireParametrageService);

  private readonly grid = viewChild(ProduitComplementaireGridComponent);

  produit: ProduitDTO;

  /** Produits déclinés complémentaires du produit : ceux que l'onglet permet d'associer à des points de livraison. */
  produitDeclinaisonComplementaireList: ProduitDeclinaisonDTO[] = [];

  readonly dialogAjoutVisible = signal<boolean>(false);

  readonly dialogPeriodeVisible = signal<boolean>(false);

  readonly produitPeriode = signal<PlcProduitComplementaireDTO>(null);

  readonly produitComplementaireList: Signal<PlcProduitComplementaireDTO[]> = this.produitComplementaireSvc.currentPageList;

  readonly totalElements = this.produitComplementaireSvc.gridTotalSignal;

  readonly currentPage = this.produitComplementaireSvc.gridPageSignal;

  readonly hasChanges = this.produitComplementaireSvc.hasChangesSignal;

  readonly produitSubscription = this.route.parent.data
      .pipe(takeUntilDestroyed())
      .subscribe((data: { produitSupplier: ProduitSupplier }) => {
        this.produit = data.produitSupplier?.produitDTO;
        this.produitDeclinaisonComplementaireList = (this.produit?.produitDeclinaisons ?? [])
          .filter(declinaison => declinaison.complementaire);
        this.produitComplementaireSvc.initProduit(this.produit?.id ?? null);
        this.grid()?.clearFiltreColonne();
      });

  canEdit = (): boolean => this.gds.canModify(this.produit);

  openDialogAjout = (): void => this.dialogAjoutVisible.set(true);

  openDialogPeriode = (produitComplementaire: PlcProduitComplementaireDTO): void => {
    this.produitPeriode.set(produitComplementaire);
    this.dialogPeriodeVisible.set(true);
  };

  onPeriodeListChange = (periodeList: PeriodeConditionnementDTO[]): void =>
    this.produitComplementaireSvc.applyPeriodeConditionnementList(this.produitPeriode(), periodeList);

  onPageIndexChange = (page: number): void => this.produitComplementaireSvc.goToPage(page);

  onSearchChanged = (event: ValueChangedEvent): void => this.produitComplementaireSvc.searchGrid(event.value ?? '');

  onLigneEditee = (): void => this.produitComplementaireSvc.collectPageEdits();

  toggleJour = (jour: JourToggle): void => this.produitComplementaireSvc.toggleJourConsommation(jour);

  supprimer = (produitComplementaire: PlcProduitComplementaireDTO): void =>
    this.produitComplementaireSvc.markDeleted(produitComplementaire);

  save = (): void => {
    this.grid()?.saveEditData().then(() => {
      const changes = this.produitComplementaireSvc.buildChanges();
      if (!changes.hasChanges()) {
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.INFO, `Aucune modification à enregistrer`);
        return;
      }

      const errorMessage = this.paramSvc.buildParametrageIncompletMessage(changes.produitComplementaireToEditList);
      if (errorMessage) {
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.ERROR, errorMessage);
        return;
      }

      this.produitComplementaireSvc.saveChanges(changes)
          .subscribe(() => this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS,
            `Produits complémentaires enregistrés avec succès`));
    });
  };
}
